/*
A re-implementation of Blueprint's `Tab` component.

This component is never actually allowed to render. Blueprint's `Tabs`
logic just uses its identity fields (e.g. `displayName`) to iterate over
its children, pick out `<Tab>` components, and render an internal component
in their place, providing additional props derived from Tabs state.

Wrapping `<Tab>` in another component (a.) breaks the child detection logic,
but more importantly (b.) is a misunderstanding that the wrapper would own the
render logic of a `Tab` -- it doesn't.

Wrapping `Tab` in a `styled()` call doesn't work because styled-components
injects it's logic into the `render()` function, which is never called.

Instead of using Blueprint's `title, icon, tagProps, tagContent` props, just
render children.

Instead of using Blueprint's `panel, panelClassName` props, just render tab
panel components based on state.
*/

export interface HexTabProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "id" | "title" | "onClick"
> {
  /**
   * Whether the tab is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * Unique identifier used to control which tab is selected
   * and to generate ARIA attributes for accessibility.
   */
  id: HexTabId;
}

export type HexTabId = string | number;

/**
 * An individual interactive tab button that toggles the corresponding panel
 * with the same `id`.
 *
 * Important:
 *
 * - Must be a direct child of `HexTabs` (e.g. `<HexTabs><HexTab /></HexTabs>`)
 * - Must NOT be wrapped in a component (e.g. NOT `const MyTab = () => <HexTab />`)
 * - Must NOT be wrapped in a `styled()` (e.g. NOT `const MyTab = styled(HexTab)`)
 *
 * @example
 *  // GOOD
 *  <HexTabs>
 *    <HexTab />
 *    <HexTab />
 *    <HexTab />
 *  </HexTabs>
 *
 *  // BAD
 *  <HexTabs>
 *    <WrappedTab />
 *    <StyledTab />
 *  </HexTabs>
 *
 *  const WrappedTab = () => <HexTab />;
 *  const StyledTab = styled(HexTab)``;
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- props are handled in `InternalHexTab`
export function HexTab(props: HexTabProps): null {
  // rendering is delegated to `InternalHexTab`
  return null;
}

HexTab.displayName = "HexTab";
